import type { TarefaRecorrencia, TarefaRecorrenciaFreq } from "@/services/tarefas";
import { formatWeekdayLong, parseDateKey } from "./dateUtils";

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
] as const;

export type RecorrenciaPreset =
  | "none"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "weekdays"
  | "custom";

export interface RecorrenciaFormState {
  preset: RecorrenciaPreset;
  freq: TarefaRecorrenciaFreq;
  interval: number;
  until: string;
}

export const DEFAULT_RECORRENCIA_FORM: RecorrenciaFormState = {
  preset: "none",
  freq: "none",
  interval: 1,
  until: "",
};

export function recorrenciaToForm(rec: TarefaRecorrencia | null | undefined): RecorrenciaFormState {
  if (!rec || rec.freq === "none") return { ...DEFAULT_RECORRENCIA_FORM };
  return {
    preset: "custom",
    freq: rec.freq,
    interval: rec.interval,
    until: rec.until ?? "",
  };
}

export function formToRecorrencia(form: RecorrenciaFormState): TarefaRecorrencia | null {
  if (form.preset === "none" || form.freq === "none") return null;
  const freq = form.preset !== "custom" ? presetToFreq(form.preset) : form.freq;
  if (!freq || freq === "none") return null;
  return {
    freq,
    interval: Math.max(1, form.interval || 1),
    until: form.until.trim() || null,
  };
}

function presetToFreq(preset: RecorrenciaPreset): TarefaRecorrenciaFreq {
  if (preset === "custom" || preset === "none") return "none";
  return preset;
}

function ordinalWeekdayLabel(dateKey: string): string {
  const { day } = parseDateKey(dateKey);
  const weekday = formatWeekdayLong(dateKey).toLowerCase();
  const weekOfMonth = Math.ceil(day / 7);
  const ordinals = ["primeira", "segunda", "terceira", "quarta", "quinta"];
  const ord = ordinals[Math.min(weekOfMonth - 1, 4)] ?? `${weekOfMonth}ª`;
  return `Mensal no(a) ${ord} ${weekday}`;
}

export function buildRecorrenciaPresetOptions(dateKey: string): Array<{
  value: RecorrenciaPreset;
  label: string;
}> {
  const { month, day } = parseDateKey(dateKey);
  const weekday = formatWeekdayLong(dateKey).toLowerCase();
  return [
    { value: "none", label: "Não se repete" },
    { value: "daily", label: "Todos os dias" },
    { value: "weekly", label: `Semanal: cada ${weekday}` },
    { value: "monthly", label: ordinalWeekdayLabel(dateKey) },
    { value: "yearly", label: `Anual em ${MONTHS[month - 1]} ${day}` },
    {
      value: "weekdays",
      label: "Todos os dias da semana (segunda a sexta-feira)",
    },
    { value: "custom", label: "Personalizar…" },
  ];
}

export function recorrenciaResumo(
  rec: TarefaRecorrencia | null | undefined,
  dateKey: string,
): string {
  if (!rec || rec.freq === "none") return "Não se repete";
  const opts = buildRecorrenciaPresetOptions(dateKey);
  const match = opts.find((o) => presetToFreq(o.value) === rec.freq && rec.interval === 1);
  if (match && match.value !== "custom") return match.label;
  const freqLabels: Record<TarefaRecorrenciaFreq, string> = {
    none: "Não se repete",
    daily: "dia",
    weekly: "semana",
    monthly: "mês",
    yearly: "ano",
    weekdays: "dia útil",
  };
  const base = `A cada ${rec.interval} ${freqLabels[rec.freq]}${rec.interval > 1 ? "s" : ""}`;
  return rec.until ? `${base} até ${rec.until.split("-").reverse().join("/")}` : base;
}

export function isTarefaRecorrente(tarefa: {
  recorrencia?: TarefaRecorrencia | null;
}): boolean {
  return !!tarefa.recorrencia && tarefa.recorrencia.freq !== "none";
}

export function getOcorrenciaData(tarefa: {
  id: string;
  data: string;
  ocorrenciaData?: string;
}): string {
  return tarefa.ocorrenciaData ?? tarefa.data;
}

export function getMasterId(tarefa: { id: string; masterId?: string }): string {
  return tarefa.masterId ?? tarefa.id.split("@")[0];
}
